import type { FeatureManifest } from './types';
import createAppointment from './agenda/create-appointment.feature';
import updateAppointment from './agenda/update-appointment.feature';
import cancelAppointment from './agenda/cancel-appointment.feature';
import manageAvailability from './agenda/manage-availability.feature';
import configureBookingSettings from './agenda/configure-booking-settings.feature';
import shareBookingLink from './agenda/share-booking-link.feature';
import viewAgenda from './agenda/view-agenda.feature';
import viewAppointmentDetail from './agenda/view-appointment-detail.feature';
import markAppointmentNoShow from './agenda/mark-appointment-no-show.feature';
import createCustomer from './customers/create-customer.feature';
import findCustomer from './customers/find-customer.feature';
import updateCustomer from './customers/update-customer.feature';
import manageTags from './customers/manage-tags.feature';
import mergeDuplicates from './customers/merge-duplicates.feature';
import readBusinessStats from './stats/read-business-stats.feature';
import manageService from './catalog/create-prestation.feature';
import manageSupplement from './catalog/manage-supplement.feature';
import manageResource from './catalog/manage-resource.feature';
import manageCategory from './catalog/manage-category.feature';
import managePack from './catalog/manage-pack.feature';
import redeemPack from './catalog/redeem-pack.feature';
import manageVoucher from './catalog/manage-voucher.feature';
import redeemVoucher from './catalog/redeem-voucher.feature';
import updateEstablishmentInfo from './establishment/update-profile.feature';
import manageEstablishmentMedia from './establishment/update-media.feature';
import manageEstablishmentPlaces from './establishment/manage-places.feature';
import manageTeamProfile from './establishment/manage-team-profile.feature';
import manageTeamAccess from './establishment/manage-team-access.feature';
import connectGoogleSync from './establishment/connect-google-sync.feature';
import personalizePulse from './establishment/personalize-pulse.feature';
import connectStripePayment from './establishment/connect-stripe-payment.feature';
import trackStripePayouts from './establishment/track-stripe-payouts.feature';
import manageSubscription from './account/manage-subscription.feature';
import viewSubscriptionInvoices from './account/view-subscription-invoices.feature';
import shareReferralLink from './account/share-referral-link.feature';
import createEmailCampaign from './marketing/create-email-campaign.feature';
import composeEmailContent from './marketing/compose-email-content.feature';
import scheduleEmailCampaign from './marketing/schedule-email-campaign.feature';
import manageBlogCategory from './marketing/manage-blog-category.feature';
import createBlogArticle from './marketing/create-blog-article.feature';
import communicationSettings from './marketing/communication-settings.feature';
import createSmsCampaign from './marketing/create-sms-campaign.feature';
import manageAutomaticMessages from './marketing/manage-automatic-messages.feature';
import seoPackOverview from './marketing/seo-pack-overview.feature';
import seoPackTopics from './marketing/seo-pack-topics.feature';
import seoPackArticles from './marketing/seo-pack-articles.feature';
import seoPackBacklinks from './marketing/seo-pack-backlinks.feature';
import seoPackOptions from './marketing/seo-pack-options.feature';
import manageWebsiteIdentity from './website/manage-identity.feature';
import manageWebsiteSeoGeo from './website/manage-seo-geo.feature';
import manageWebsiteRedirects from './website/manage-redirects.feature';
import configureWebsiteTracking from './website/configure-tracking.feature';
import editWebsiteBuilder from './website/edit-builder.feature';
import manageWebsiteBuilderPages from './website/manage-builder-pages.feature';
import manageWebsiteBuilderStyle from './website/manage-builder-style.feature';
import manageWebsiteBuilderComponent from './website/manage-builder-component.feature';
import publishWebsiteBuilder from './website/publish-builder.feature';
import connectDomain from './website/connect-domain.feature';
import configureEmail from './website/configure-email.feature';
import listPayments from './sales/list-payments.feature';
import listVouchers from './sales/list-vouchers.feature';

export * from './types';
export * from './define-feature';
export * from './validate';
export * from './test-ids';
export * from './operation-ids.generated';
export * from './narration';

/**
 * Registre agrégé des manifestes de features. Liste explicite (pas de glob,
 * fragile selon le bundler) — à étendre à la main quand un manifeste est ajouté.
 * Cf. docs/documentation-strategy.md § 9.3.
 */
export const FEATURE_MANIFESTS: FeatureManifest[] = [
  createAppointment,
  updateAppointment,
  cancelAppointment,
  manageAvailability,
  configureBookingSettings,
  shareBookingLink,
  viewAgenda,
  viewAppointmentDetail,
  markAppointmentNoShow,
  createCustomer,
  findCustomer,
  updateCustomer,
  manageTags,
  mergeDuplicates,
  readBusinessStats,
  manageService,
  manageSupplement,
  manageResource,
  manageCategory,
  managePack,
  redeemPack,
  manageVoucher,
  redeemVoucher,
  updateEstablishmentInfo,
  manageEstablishmentMedia,
  manageEstablishmentPlaces,
  manageTeamProfile,
  manageTeamAccess,
  connectGoogleSync,
  personalizePulse,
  connectStripePayment,
  trackStripePayouts,
  manageSubscription,
  viewSubscriptionInvoices,
  shareReferralLink,
  manageBlogCategory,
  createBlogArticle,
  communicationSettings,
  createEmailCampaign,
  composeEmailContent,
  scheduleEmailCampaign,
  createSmsCampaign,
  manageAutomaticMessages,
  seoPackOverview,
  seoPackTopics,
  seoPackArticles,
  seoPackBacklinks,
  seoPackOptions,
  manageWebsiteIdentity,
  manageWebsiteSeoGeo,
  manageWebsiteRedirects,
  configureWebsiteTracking,
  editWebsiteBuilder,
  manageWebsiteBuilderPages,
  manageWebsiteBuilderStyle,
  manageWebsiteBuilderComponent,
  publishWebsiteBuilder,
  connectDomain,
  configureEmail,
  listPayments,
  listVouchers,
];

/** Index `id → FeatureManifest` pour lookup O(1) (doc, MCP, runner E2E). */
export const FEATURE_MANIFEST_BY_ID: ReadonlyMap<string, FeatureManifest> = new Map(
  FEATURE_MANIFESTS.map((manifest): [string, FeatureManifest] => [manifest.id, manifest]),
);

/**
 * Index `operationId → manifeste` (endpoint principal ET endpoints liés — le
 * principal gagne en cas de collision). Sert de REPLI aux descriptions des
 * tools MCP : une route sans `@McpFeature` résolu ni summary Swagger hérite de
 * la doc du manifeste qui la référence — même source que le docs-site.
 */
export const FEATURE_MANIFEST_BY_OPERATION_ID: ReadonlyMap<string, { manifest: FeatureManifest; is_primary: boolean }> =
  ((): ReadonlyMap<string, { manifest: FeatureManifest; is_primary: boolean }> => {
    const map = new Map<string, { manifest: FeatureManifest; is_primary: boolean }>();
    for (const manifest of FEATURE_MANIFESTS) {
      for (const operationId of manifest.api.related_operation_ids ?? []) {
        if (!map.has(operationId)) map.set(operationId, { manifest, is_primary: false });
      }
    }
    for (const manifest of FEATURE_MANIFESTS) {
      map.set(manifest.api.primary_operation_id, { manifest, is_primary: true });
    }
    return map;
  })();
